import React, { useEffect } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import * as Routes from 'routes';
import { zcareTheme } from 'zcareTheme';
import { ThemeProvider } from '@material-ui/core/styles';
import UserProfileContext from './context/userProfileContext';
import { StoreProvider } from 'on_web/helpers/store';

import LoginPage from './pages/LoginPage';
import Auth0CallbackPage from './pages/Auth0CallbackPage';
import PathSelectionPage from './pages/PathSelectionPage';

import OnboardingIntroPage from './pages/OnboardingIntroPage';
import OnboardingDnaPage from './pages/OnboardingDnaPage';
import OnboardingDnaKitInstructionsPage from './pages/OnboardingDnaKitInstructionsPage';
import OnboardingGeneral from './pages/OnboardingGeneralPage';
import OnboardingLifestylePage from './pages/OnboardingLifestylePage';
import OnboardingDiseasesPage from './pages/OnboardingDiseasesPage';
import OnboardingEnvironmentPage from './pages/OnboardingEnvironmentPage';
import OnboardingEndPage from './pages/OnboardingEndPage';
import PceOnboardingIntroPage from './pages/PceOnboardingIntroPage';
import PceOnboardingDnaPage from './pages/PceOnboardingDnaPage';
import PceOnboardingGeneral from './pages/PceOnboardingGeneralPage';
import PceOnboardingSkinCarePage from './pages/PceOnboardingSkinCarePage';
import ReportsMyAssessment from './pages/ReportsMyAssessment';
import ReportsDnaAssessment from './pages/ReportsDnaAssessment';
import ReportsEnterpiseDemo from './pages/ReportsEnterpiseDemo';
import ReportsAssessmentRecommendations from './pages/ReportsAssessmentRecommendations';
import ReportsTraitGroup from './pages/ReportsTraitGroup';
import ReportsTraitDetails from './pages/ReportsTraitDetails';
import ReportsComorbidityGroup from './pages/ReportsComorbidityGroup';
import ReportsComorbidityDetails from './pages/ReportsComorbidityDetails';
import ReportsComorbiditiesOverview from './pages/ReportsComorbiditiesOverview';
import ReportsTermsOfService from 'on_web/pages/TermsOfService';
import ResearchPage from 'on_web/pages/ResearchPage';
import FaqPage from 'on_web/pages/FaqPage';
import ShareAppPage from 'on_web/pages/ShareAppPage';
import LandingPage from 'on_web/pages/LandingPage';
import LandingAlternatePage from 'on_web/pages/LandingAlternatePage';
import HealthProfilePage from 'on_web/pages/HealthProfilePage';
import ReportsLikelihood from 'on_web/pages/ReportsLikelihood';

/* JUST DURING TESTING START */
import ResetUserData from './pages/ResetUserData';
import DummySentryTestPage from './pages/DummySentryTestPage';
/* JUST DURING TESTING END */

import RequireAuthentication from './helpers/RequireAuthentication';
import RetailExperienceCart from './pce_legacy/src/components/pages/RetailExperienceCart/RetailExperienceCart';
import Benefits from './pce_legacy/src/components/pages/Benefits/Benefits';
import ErrorMessageTile from 'on_web/components/shared/ErrorMessageTile/ErrorMessageTile';

import * as Sentry from '@sentry/react';
import { trackPageVisit } from 'on_web/helpers/userTrackingEventsHelper';

function FallbackComponent() {
  return (
    <ErrorMessageTile
      isPage
      level="error"
      actionText="RELOAD"
      onButtonClick={() => {
        window.location = '/';
      }}
    ></ErrorMessageTile>
  );
}

function App() {
  if (
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    // Sentry DSN that can be found here https://sentry.io/settings/lifenome-t0/projects/demo-app/keys/
    Sentry.init({
      dsn:
        process.env.REACT_APP_SENTRY_DSN ||
        'https://8a7999cdbfdb4a4fa5e1b0a00ed40039@o432785.ingest.sentry.io/5386217',
    });
  }

  function usePageViews() {
    let location = useLocation();

    useEffect(() => {
      trackPageVisit(location.pathname);
    }, [location]);
  }

  usePageViews();
  return (
    <Sentry.ErrorBoundary fallback={FallbackComponent}>
      <ThemeProvider theme={zcareTheme}>
        <UserProfileContext.Provider
          value={{
            showBasicQuestions:
              process.env.REACT_APP_SHOW_BASIC_QUESTIONS === 'true',
          }}
        >
          <Switch>
            {/*<Route exact path={Routes.ROUTE_DEFAULT} component={LoginPage} />*/}
            <Route exact path={Routes.ROUTE_DEFAULT} component={LandingPage} />
            <Route
              exact
              path={Routes.ROUTE_DEFAULT_ALTERNATE}
              component={LandingAlternatePage}
            />
            <Route
              exact
              path={Routes.ROUTE_AUTH0_CALLBACK}
              component={Auth0CallbackPage}
            />
            <Route
              exact
              path={Routes.ROUTE_ACCOUNT_VERIFIED}
              component={Auth0CallbackPage}
            />
            <Route
              exact
              path={Routes.ROUTE_SENTRY_DUMMY_TEST}
              component={DummySentryTestPage}
            />
            <Route
              exact
              path={Routes.ROUTE_LANDING_PAGE}
              component={LandingPage}
            />
            <Route
              exact
              path={Routes.ROUTE_PATH_SELECTION}
              component={RequireAuthentication(PathSelectionPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_INTRO}
              component={RequireAuthentication(OnboardingIntroPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_PCE_ONBOARDING_INTRO}
              component={RequireAuthentication(PceOnboardingIntroPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_PCE_ONBOARDING_DNA}
              component={RequireAuthentication(PceOnboardingDnaPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_DNA_INSTRUCTIONS}
              component={RequireAuthentication(
                OnboardingDnaKitInstructionsPage
              )}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_GENERAL}
              component={RequireAuthentication(OnboardingGeneral)}
            />
            <Route
              exact
              path={Routes.ROUTE_PCE_ONBOARDING_GENERAL}
              component={RequireAuthentication(PceOnboardingGeneral)}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_ENVIRONMENT}
              component={RequireAuthentication(OnboardingEnvironmentPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_LIFESTYLE}
              component={RequireAuthentication(OnboardingLifestylePage)}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_DISEASES}
              component={RequireAuthentication(OnboardingDiseasesPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_ONBOARDING_END}
              component={RequireAuthentication(OnboardingEndPage)}
            />
            <Route
              exact
              path={Routes.ROUTE_PCE_ONBOARDING_SKINCARE}
              component={RequireAuthentication(PceOnboardingSkinCarePage)}
            />
            <Route
              exact
              path={Routes.ROUTE_RESET_USER_DATA}
              component={RequireAuthentication(ResetUserData)}
            />
            <Route
              exact
              path={Routes.ROUTE_RETAIL_EXPERIENCE_CART}
              component={RequireAuthentication(RetailExperienceCart)}
            />
            <Route
              exact
              path={Routes.ROUTE_BENEFITS}
              component={RequireAuthentication(Benefits)}
            />
            <StoreProvider>
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_HOME}
                component={RequireAuthentication(ReportsMyAssessment)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_DNA_TRAIT_GROUP}
                component={RequireAuthentication(ReportsTraitGroup)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_DNA_TRAIT_DETAILS}
                component={RequireAuthentication(ReportsTraitDetails)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_HEALTH_RISKS_DETAILS}
                component={RequireAuthentication(ReportsComorbidityDetails)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_HEALTH_RISKS_GROUPS}
                component={RequireAuthentication(ReportsComorbidityGroup)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_HEALTH_RISKS}
                component={RequireAuthentication(ReportsComorbiditiesOverview)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_DNA_RISKS}
                component={RequireAuthentication(ReportsDnaAssessment)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_ENTERPRISE_DEMO}
                component={RequireAuthentication(ReportsEnterpiseDemo)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_RECOMMENDATIONS}
                component={RequireAuthentication(
                  ReportsAssessmentRecommendations
                )}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_LIKELIHOOD}
                component={RequireAuthentication(ReportsLikelihood)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_TERMS_OF_SERVICE}
                component={RequireAuthentication(ReportsTermsOfService)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_RESEARCH}
                component={RequireAuthentication(ResearchPage)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_FAQ}
                component={RequireAuthentication(FaqPage)}
              />
              <Route
                exact
                path={Routes.ROUTE_ASSESSMENT_SHARE_APP}
                component={RequireAuthentication(ShareAppPage)}
              />
              <Route
                exact
                path={Routes.ROUTE_HEALTH_PROFILE}
                component={RequireAuthentication(HealthProfilePage)}
              />
              <Route
                exact
                path={Routes.ROUTE_ONBOARDING_DNA}
                component={RequireAuthentication(OnboardingDnaPage)}
              />
            </StoreProvider>
          </Switch>
        </UserProfileContext.Provider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
